/**
 * Persistent ring-buffer for diagnostic events. Backed by localStorage
 * so the buffer survives the page reload that's currently the only
 * known workaround for bug #1 (game unresponsive after game-switch).
 *
 * Designed for low-frequency, high-signal events — instrumenting hot
 * paths (per-frame, per-tap) would balloon the buffer past its cap.
 * Use sparingly and remove TODO-marked sites once a bug is diagnosed.
 *
 * The buffer is included in the BetaDiagnosticModal's "Copy diagnostic"
 * output so a tester who hits an intermittent bug can paste the
 * recent history to a bug report without having had DevTools open.
 *
 * Pure module — safe to import without bringing in Phaser. Falls back
 * to in-memory only when localStorage is unavailable (private mode,
 * quota exceeded).
 */

const STORAGE_KEY = 'battleshark.bugLog.v1';
const MAX_EVENTS = 50;

export interface BugLogEvent {
  /** ISO 8601 timestamp. */
  readonly ts: string;
  /** Short tag identifying the bug under investigation. Convention:
   *  `[bug-N]` where N matches the issue tracker / commit message. */
  readonly tag: string;
  /** Short human-readable description of where the event was emitted
   *  from (e.g. "RemoteGameStore ctor", "GameScene SHUTDOWN"). */
  readonly message: string;
  /** Arbitrary JSON-serializable context. Keep fields short — the
   *  whole buffer ships in the diagnostic copy/paste. */
  readonly data: Record<string, unknown>;
}

let buffer: BugLogEvent[] = loadFromStorage();

/**
 * Append an event to the buffer and persist. Also mirrors to
 * console.log so the live DevTools view still sees it.
 */
export function logBugEvent(
  tag: string,
  message: string,
  data: Record<string, unknown>
): void {
  const event: BugLogEvent = {
    ts: new Date().toISOString(),
    tag,
    message,
    data,
  };
  buffer.push(event);
  if (buffer.length > MAX_EVENTS) {
    buffer = buffer.slice(-MAX_EVENTS);
  }
  saveToStorage();
  // Mirror to console for anyone with DevTools open. The buffer is
  // primarily for the "I noticed the bug 5 minutes ago, let me check"
  // case where the console history is gone.
  // eslint-disable-next-line no-console
  console.log(tag, message, data);
}

/** Read-only snapshot of the buffer. Returns a new array; mutations
 *  don't affect the underlying buffer. */
export function getBugLogEvents(): readonly BugLogEvent[] {
  return [...buffer];
}

/** Empty the buffer. Wired to a "Clear" affordance in the diagnostic
 *  modal so testers can capture a clean repro window. */
export function clearBugLog(): void {
  buffer = [];
  saveToStorage();
}

function loadFromStorage(): BugLogEvent[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isBugLogEvent).slice(-MAX_EVENTS);
  } catch {
    return [];
  }
}

function saveToStorage(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch {
    // Quota exceeded or private mode — best effort, console.log path
    // still works for anyone with DevTools open.
  }
}

function isBugLogEvent(x: unknown): x is BugLogEvent {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o['ts'] === 'string' &&
    typeof o['tag'] === 'string' &&
    typeof o['message'] === 'string' &&
    typeof o['data'] === 'object' &&
    o['data'] !== null
  );
}
